import { z } from 'zod/v4';

/**
 * RRO (Ukrainian fiscalisation) line-item info attached to a payment element.
 * Mirrors `RroInfoContract` from `@integrations/payment-methods` — every leaf
 * is optional/nullish because the backend only fills it for fiscalised methods.
 */
export const rroInfoSchema = z.object({
    price: z.number().nullish(),
    cost: z.number().nullish(),
    amount: z.number().nullish(),
    id: z.union([z.number(), z.string()]).nullish(),
});

/**
 * One configured payment method on the form — mirrors `PaymentElementContract`
 * from `@integrations/payment-methods`. `type` is the numeric `EPaymentType`
 * value; `amount` is the price. The `customPaymentName`/`newPaymentName`/
 * `eduPaymentMethodId` extras only exist for custom or EDU-linked methods, so
 * they stay optional.
 */
export const paymentElementSchema = z.object({
    type: z.number(),
    amount: z.number(),
    currency: z.string().nullish(),
    payment_method_id: z.string().nullish(),
    isEditableTotalCost: z.boolean().nullish(),
    isActive: z.boolean().nullish(),
    rro_isActive: z.boolean().nullish(),
    rro_info: rroInfoSchema.nullish(),
    customPaymentName: z.string().nullish(),
    newPaymentName: z.string().nullish(),
    eduPaymentMethodId: z.number().nullish(),
});

export type RroInfoLegacy = z.infer<typeof rroInfoSchema>;
export type PaymentElementLegacy = z.infer<typeof paymentElementSchema>;

/**
 * A fully-populated payment element — the entity twin of the legacy
 * `paymentElementFormGroup()` factory, which likewise materialised **every**
 * control up front.
 *
 * Every optional key is emitted as an explicit `null` rather than omitted: a
 * signal-form `FieldTree` only materialises children for keys present in the
 * model (the missing-key trap), so an omitted key leaves `field().<key>`
 * `undefined` and `[formField]` with nothing to bind — and validity computed
 * off that key (`customPaymentName`) latches invalid with no control on screen
 * to clear it. Callers that build elements at runtime (the payment-methods
 * collections) must go through here rather than hand-rolling a literal.
 */
export const createPaymentElementLegacy = (overrides: Partial<PaymentElementLegacy> = {}): PaymentElementLegacy => ({
    // `0` is `EPaymentType.payPal` — the host enum lives outside this package.
    type: 0,
    amount: 0,
    currency: 'USD',
    payment_method_id: null,
    isEditableTotalCost: false,
    isActive: true,
    rro_isActive: false,
    rro_info: { id: null, cost: null, amount: 1, price: null },
    customPaymentName: '',
    newPaymentName: null,
    eduPaymentMethodId: null,
    ...overrides,
});
